import React from 'react';
import type { RaceNight } from '../../models/raceNights/RaceNight';
import {
  dateParts, isActionableNight, nightPill, rigsOffered, windowLabel, WEBSITE_MEMBERSHIPS, type NightPillTone,
} from '../../utils/raceNightFormat';

interface Props {
  night: RaceNight;
  open: boolean;
  onToggle: () => void;
}

const RIG_LABEL = { pro: 'Pro', esports: 'Esports' } as const;

/**
 * Seats left per rig class, in the same tags the time rows use (SlotRows.css). A class the night has
 * no rigs for isn't drawn: "sold out" would claim seats that never existed.
 */
const RigTags: React.FC<{ night: RaceNight }> = ({ night }) => (
  <>
    {rigsOffered(night).map((rig) => {
      const left = rig === 'pro' ? night.seats.proLeft : night.seats.esportsLeft;
      const label = RIG_LABEL[rig];
      return (
        <span key={rig} className={`rig-tag ${left === 0 ? 'sold-out' : left <= 2 ? 'limited' : 'available'}`}>
          {left === 0 ? `${label} · sold out` : `${label} · ${left} left`}
        </span>
      );
    })}
  </>
);

/** Who can come, under the pill. */
const accessHint = (night: RaceNight): string => {
  if (night.seats.open === 0) return 'Full · join the waitlist';
  if (night.access === 'membersOnly') return 'Members only';
  return night.access === 'public' ? 'Guests welcome' : '';
};

/** The pill's class on the page: a card night's price is a plain figure here, not a pill. */
export const PILL_CLASS: Record<NightPillTone, string> = {
  in: 'rn-pill rn-in',
  wait: 'rn-pill rn-wait',
  lock: 'rn-pill rn-lock',
  price: 'rn-price',
  credits: 'rn-pill rn-credits',
  included: 'rn-pill rn-included',
};

/** The line under the pill: the member's own seat first, then who can come. */
const Hint: React.FC<{ night: RaceNight }> = ({ night }) => {
  if (night.mine === 'seated') return <span className="rn-hint">{night.myRig === 'esports' ? 'Esports rig' : 'Pro rig'}</span>;
  if (night.mine === 'paymentPending') return <span className="rn-hint">Seats held, not booked</span>;
  if (night.mine === 'waitlisted') return <span className="rn-hint">The venue will contact you</span>;
  switch (night.treatment) {
    case 'locked':
      return <a className="rn-hint rn-link" href={WEBSITE_MEMBERSHIPS} target="_blank" rel="noreferrer">See memberships</a>;
    case 'card':
    case 'credits':
      return <span className="rn-hint">{accessHint(night)}</span>;
    default:
      return <span className="rn-hint">{night.seats.open === 0 ? 'Full · join the waitlist' : night.access === 'public' ? 'Guests welcome' : ''}</span>;
  }
};

/** The pill (shared with the home, via nightPill) and the line under it: what this night is to THIS member. */
const Side: React.FC<{ night: RaceNight }> = ({ night }) => {
  const pill = nightPill(night);
  return (
    <>
      <span className={PILL_CLASS[pill.tone]}>{pill.label}</span>
      <Hint night={night} />
    </>
  );
};

/** One night. A night this member can act on is a button; a teaser is plain text with its own link. */
const RaceNightRow: React.FC<Props> = ({ night, open, onToggle }) => {
  const p = dateParts(night.date);
  const inner = (
    <>
      <div className="rn-date">
        <span className="rn-dow">{p.dow}</span>
        <span className="rn-day">{p.day}</span>
        <span className="rn-dow">{p.month}</span>
      </div>
      <div className="rn-info">
        <span className="rn-series">{night.seriesName}</span>
        <span className="rn-title">{night.title}</span>
        {night.description && <span className="rn-desc">{night.description}</span>}
        <div className="rn-meta">
          <span className="rn-time">{windowLabel(night.startTime, night.endTime)}</span>
          <RigTags night={night} />
        </div>
      </div>
      <div className="rn-side"><Side night={night} /></div>
    </>
  );

  // Every treatment but locked is claimable here: included, credits and card (members and non-members alike).
  const actionable = isActionableNight(night);
  return actionable ? (
    <button type="button" className={`rn-row${open ? ' rn-open' : ''}`} aria-expanded={open} onClick={onToggle}
      data-testid={`night-${night.occurrenceId}`}>
      {inner}
    </button>
  ) : (
    <div className="rn-row rn-teaser" data-testid={`night-${night.occurrenceId}`}>{inner}</div>
  );
};

export default RaceNightRow;
